/**
 * Main navigation sidebar component
 *
 * Renders a responsive sidebar with navigation items, theme toggle, and optional header/footer content.
 * Supports both light and dark themes with appropriate styling.
 *
 * @example
 * ```tsx
 * <Sidebar
 *   options={[
 *     { label: 'Dashboard', href: '/dashboard', icon: HomeIcon },
 *     { label: 'Settings', href: '/settings', icon: SettingsIcon }
 *   ]}
 *   header={<Logo />}
 * />
 * ```
 */
import { ChevronLeftIcon } from 'lucide-react';
import { AnimatePresence, HTMLMotionProps, motion } from 'motion/react';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { SidebarItem } from '@core/components/navigation/Sidebar/SidebarItem';
import { ThemeButton } from '@core/components/navigation/ThemeButton';
import { Text } from '@core/components/Text';
import { NavItem } from '@core/hooks/useNavItems';
import { cn } from '@core/utils/styles';

import { Button } from '../../Button';

/**
 * Props for the Sidebar component
 * @interface SidebarProps
 * @extends {HTMLMotionProps<"nav">}
 */
interface SidebarProps extends HTMLMotionProps<'nav'> {
  /** Optional header content to be displayed at the top of the sidebar */
  header?: React.ReactNode;
  /** Whether the sidebar is expanded or collapsed */
  expanded?: boolean;
  /** Function to set the expanded state of the sidebar */
  setExpanded?: (expanded: boolean) => void;
  /** Array of navigation items to be displayed in the sidebar */
  options?: NavItem[];
  /** Optional footer content to be displayed at the bottom of the sidebar */
  footer?: React.ReactNode;
}
export function Sidebar({
  className,
  header,
  options = [],
  expanded = true,
  setExpanded,
  footer,
  ...props
}: SidebarProps) {
  return (
    <motion.nav
      {...props}
      aria-label="Main navigation"
      className={cn(
        'bg-back-light ring-stroke-light dark:bg-back-dark rounded-r-theme absolute z-8 flex h-full min-h-0 flex-col ring-1 shadow-sm transition-all md:relative md:z-10 dark:ring-white/10',
        expanded ? 'left-0' : '-left-20 md:left-0',
        className
      )}
      initial={{ width: '5rem' }}
      animate={{ width: expanded ? '16rem' : '5rem' }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      {header}

      <div className="flex-1">
        <SidebarItems items={options} expanded={expanded} setExpanded={setExpanded} />
      </div>

      {footer}

      <ThemeButton className="absolute bottom-18 left-1/2 -translate-x-1/2" />

      {setExpanded && (
        <div
          className={cn(
            'absolute top-6 -right-1 hidden translate-x-1/2 items-center justify-center transition-all duration-300 md:flex'
          )}
        >
          <Button
            onClick={() => setExpanded(!expanded)}
            size="icon-sm"
            variant="interactive"
            className="rounded-full"
          >
            <ChevronLeftIcon
              strokeWidth={2.5}
              className={cn('size-5 transition-all', expanded ? '-ml-0.5' : 'rotate-180')}
            />
          </Button>
        </div>
      )}
    </motion.nav>
  );
}

/**
 * Internal component that renders the list of navigation items
 * Handles hover states and active item highlighting
 *
 * @internal
 */
const SidebarItems = ({
  items,
  expanded = true,
  setExpanded,
}: {
  items: NavItem[];
  expanded?: boolean;
  setExpanded?: (expanded: boolean) => void;
}) => {
  const location = useLocation();
  const [focused, setFocused] = useState<string | null>(null);

  return (
    <div
      onMouseLeave={() => setFocused(null)}
      onBlur={() => setFocused(null)}
      className={cn('flex flex-col gap-2 py-4 pr-4 transition-all', expanded ? 'pr-4' : 'pr-3')}
      role="menu"
    >
      {items.map((item) => (
        <SidebarItem
          key={item.label}
          href={item.href}
          current={location.pathname.startsWith(item.href)}
          focused={focused === item.href}
          onFocus={() => setFocused(item.href)}
          onMouseEnter={() => setFocused(item.href)}
          onClick={() => setExpanded && setExpanded(false)}
          className={cn('h-9 transition-all', expanded ? 'ml-4' : 'ml-3')}
        >
          <item.icon
            className={cn(
              'text-txt-light-muted dark:text-txt-dark-muted absolute top-2 size-5 transition-all',
              expanded ? 'left-3' : 'left-4.5',
              location.pathname.startsWith(item.href) && 'text-txt-light dark:text-txt-dark'
            )}
            aria-hidden="true"
          />
          <AnimatePresence>
            {expanded && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, delay: 0.2 }}
                className="ml-8"
              >
                <Text size="sm" weight="medium">
                  {item.label}
                </Text>
              </motion.span>
            )}
          </AnimatePresence>
        </SidebarItem>
      ))}
    </div>
  );
};
